import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Gift } from "lucide-react";
import { getProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const fmt = (n) => `$${n.toFixed(2)} AUD`;
const MAX = 5;

const tierFor = (n) => {
    if (n >= 5) return { rate: 0.2, label: "5 bars · save 20%" };
    if (n >= 3) return { rate: 0.15, label: "3 bars · save 15%" };
    return { rate: 0, label: null };
};

const BundleBuilder = () => {
    const [products, setProducts] = useState([]);
    const [selected, setSelected] = useState([]);
    const [wantGiftBox, setWantGiftBox] = useState(false);
    const { add, setGiftBox, setOpen } = useCart();

    useEffect(() => {
        getProducts().then(setProducts).catch(() => {});
    }, []);

    const productMap = useMemo(() => {
        const m = {};
        products.forEach((p) => (m[p.slug] = p));
        return m;
    }, [products]);

    const toggle = (slug) => {
        if (selected.includes(slug)) {
            setSelected((s) => s.filter((x) => x !== slug));
        } else if (selected.length < MAX) {
            setSelected((s) => [...s, slug]);
        } else {
            toast(`You've chosen ${MAX} bars. Remove one to swap.`, { duration: 2500 });
        }
    };

    const items = selected.map((slug) => productMap[slug]).filter(Boolean);
    const subtotal = items.reduce((s, p) => s + p.price, 0);
    const tier = tierFor(items.length);
    const discount = subtotal * tier.rate;
    const giftBoxFee = wantGiftBox ? 8 : 0;
    const total = subtotal - discount + giftBoxFee;
    const isTier = items.length >= 3;

    const addBundle = () => {
        if (!isTier) return;
        items.forEach((p) => add(p, 1));
        if (wantGiftBox) setGiftBox(true);
        toast.success("Bundle added to your cart.");
        setOpen(true);
    };

    return (
        <div data-testid="bundle-builder-page" className="bg-[#FAF9F6]">
            <section className="px-6 lg:px-10 pt-16 pb-12">
                <div className="max-w-7xl mx-auto">
                    <p className="label">Build Your Own Bundle</p>
                    <h1 className="font-serif text-5xl sm:text-6xl mt-5 leading-[1.04]">
                        Choose your bars.
                    </h1>
                    <p className="mt-6 max-w-xl text-lg text-[#4A4A46] leading-relaxed">
                        Pick any three bars for $63.75. Pick any five for $100. Bundle pricing is
                        applied automatically.
                    </p>
                </div>
            </section>

            {/* Progress + tier indicators */}
            <section className="px-6 lg:px-10 pb-10 sticky top-20 z-30 bg-[#FAF9F6]/95 backdrop-blur border-b border-[#E3DED5]">
                <div className="max-w-7xl mx-auto py-5 flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <p className="label">Your bundle</p>
                        <div className="flex items-center gap-2" data-testid="bundle-progress">
                            {Array.from({ length: MAX }).map((_, i) => {
                                const filled = i < selected.length;
                                const milestone = i === 2 || i === 4;
                                return (
                                    <div
                                        key={`slot-${i}`}
                                        className={`w-9 h-9 border flex items-center justify-center text-[11px] uppercase tracking-[0.15em] ${
                                            filled
                                                ? "bg-[#1C1C1A] border-[#1C1C1A] text-[#FAF9F6]"
                                                : milestone
                                                ? "border-[#6A7059] text-[#6A7059]"
                                                : "border-[#C7C0B3] text-[#737562]"
                                        }`}
                                        data-testid={`slot-${i}-${filled ? "filled" : "empty"}`}
                                    >
                                        {filled ? <Check size={14} /> : i + 1}
                                    </div>
                                );
                            })}
                        </div>
                        <p className="text-sm text-[#4A4A46] hidden sm:block" data-testid="bundle-tier">
                            {tier.label || `Pick ${3 - items.length} more for $63.75`}
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-[#4A4A46]" data-testid="bundle-running-total">
                            {fmt(total)}
                        </span>
                        <button
                            onClick={addBundle}
                            disabled={!isTier}
                            data-testid="add-bundle-button"
                            className={`btn-primary ${!isTier ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                            Add Bundle
                        </button>
                    </div>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-8">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-12" data-testid="bundle-products-grid">
                        {products.map((p) => {
                            const isSelected = selected.includes(p.slug);
                            return (
                                <button
                                    type="button"
                                    key={p.slug}
                                    onClick={() => toggle(p.slug)}
                                    data-testid={`bundle-pick-${p.slug}`}
                                    className="group text-left relative"
                                >
                                    <div
                                        className={`product-image-wrap aspect-[4/5] relative transition-all duration-300 ${
                                            isSelected ? "ring-1 ring-[#1C1C1A]" : ""
                                        }`}
                                    >
                                        <img
                                            src={p.images?.[0]}
                                            alt={p.name}
                                            loading="lazy"
                                            className="product-image w-full h-full object-cover"
                                        />
                                        {isSelected && (
                                            <div className="absolute top-3 right-3 w-8 h-8 bg-[#1C1C1A] text-[#FAF9F6] flex items-center justify-center">
                                                <Check size={14} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-4 flex items-start justify-between gap-3">
                                        <div>
                                            <h3 className="font-serif text-lg leading-tight text-[#1C1C1A]">
                                                {p.name}
                                            </h3>
                                            <p className="text-xs uppercase tracking-[0.18em] text-[#737562] mt-1">
                                                {p.fragrance_status || p.category.replace("-", " ")}
                                            </p>
                                        </div>
                                        <p className="text-sm whitespace-nowrap">
                                            ${p.price.toFixed(2)}
                                        </p>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <aside className="lg:col-span-4">
                    <div className="bg-[#F0EBE1] p-8 lg:sticky lg:top-44">
                        <h2 className="font-serif text-2xl">Your bundle</h2>
                        <p className="mt-2 text-sm text-[#737562]">
                            Three bars for $63.75. Five for $100.
                        </p>

                        <div className="mt-6 space-y-4 min-h-[180px]" data-testid="bundle-summary-list">
                            {items.length === 0 && (
                                <p className="text-[#4A4A46] text-sm italic">
                                    No bars chosen yet. Pick three to unlock the bundle.
                                </p>
                            )}
                            {items.map((p, i) => (
                                <div
                                    key={`${p.slug}-${i}`}
                                    className="flex gap-4"
                                    data-testid={`bundle-summary-item-${p.slug}`}
                                >
                                    <div className="w-14 h-16 product-image-wrap shrink-0">
                                        <img
                                            src={p.images?.[0]}
                                            alt={p.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="flex-1 flex items-start justify-between">
                                        <div>
                                            <p className="font-serif text-base leading-tight">
                                                {p.name}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => toggle(p.slug)}
                                                className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#737562] hover:text-[#1C1C1A]"
                                                data-testid={`bundle-remove-${p.slug}`}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                        <p className="text-sm">${p.price.toFixed(2)}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <label className="mt-6 flex items-start gap-3 cursor-pointer pt-6 border-t border-[#C7C0B3]">
                            <input
                                type="checkbox"
                                checked={wantGiftBox}
                                onChange={(e) => setWantGiftBox(e.target.checked)}
                                className="mt-1 accent-[#1C1C1A]"
                                data-testid="bundle-gift-box-toggle"
                            />
                            <span className="flex-1 text-sm leading-relaxed">
                                <span className="flex items-center gap-2">
                                    <Gift size={14} strokeWidth={1.4} className="text-[#6A7059]" />
                                    Add a simple gift box <span className="text-[#737562]">+$8.00</span>
                                </span>
                                <span className="block text-[#737562] text-xs mt-1">
                                    Unbleached paper, wrapped quietly.
                                </span>
                            </span>
                        </label>

                        <div className="mt-6 pt-6 border-t border-[#C7C0B3] text-sm space-y-2">
                            <div className="flex justify-between">
                                <span className="text-[#4A4A46]">Subtotal</span>
                                <span data-testid="bundle-subtotal">{fmt(subtotal)}</span>
                            </div>
                            {tier.rate > 0 && (
                                <div
                                    className="flex justify-between text-[#6A7059]"
                                    data-testid="bundle-discount-line"
                                >
                                    <span>Bundle &middot; {tier.label}</span>
                                    <span>-{fmt(discount)}</span>
                                </div>
                            )}
                            {wantGiftBox && (
                                <div className="flex justify-between">
                                    <span className="text-[#4A4A46]">Gift box</span>
                                    <span>{fmt(giftBoxFee)}</span>
                                </div>
                            )}
                            <div className="flex justify-between pt-3 border-t border-[#C7C0B3] text-base font-medium">
                                <span>Total</span>
                                <span data-testid="bundle-total">{fmt(total)}</span>
                            </div>
                        </div>

                        <button
                            onClick={addBundle}
                            disabled={!isTier}
                            className={`btn-primary w-full mt-6 ${
                                !isTier ? "opacity-40 cursor-not-allowed" : ""
                            }`}
                            data-testid="add-bundle-summary-button"
                        >
                            {isTier
                                ? "Add Bundle to Cart"
                                : `Pick ${3 - items.length} more`}
                        </button>
                        <p className="mt-3 text-[11px] text-[#737562] text-center">
                            Free shipping over $80 within Australia.
                        </p>
                    </div>
                </aside>
            </div>

            <section className="bg-[#F0EBE1] py-20 px-6 lg:px-10">
                <div className="max-w-3xl mx-auto text-center">
                    <p className="label">A small note</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-snug">
                        Thoughtfully chosen.
                    </h2>
                    <p className="mt-5 text-[#4A4A46] leading-relaxed">
                        Bundles are a quiet way to try a few bars or to share something you
                        trust. Every bar is handmade in Darwin and wrapped in simple paper.
                    </p>
                    <div className="mt-10">
                        <Link to="/shop" className="btn-secondary" data-testid="bundle-back-to-shop">
                            Or browse all soap
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BundleBuilder;
